/**
 * PlacesAutocompleteInput
 * A styled Google Places address autocomplete field that visually matches
 * the project's MDInput fields.
 *
 * Uses attachAutocomplete() from utils/places.js — the same singleton
 * @googlemaps/js-api-loader that powers task-create. This avoids the
 * multi-instance race condition in react-google-autocomplete where only
 * the first instance on a page receives the Places dropdown.
 *
 * Props:
 *   label             - Optional label string rendered above the input
 *   value             - Controlled string value
 *   onChange(str)     - Called on every keystroke and after place selection
 *   placeholder       - Input placeholder text
 *   required          - Native required attribute
 *   recentSuggestions - Array of { address, clientName, facilityName } from
 *                       the local address book.  Displayed in a dropdown
 *                       above the Google Places pac-container so dispatchers
 *                       can reuse previous entries without retyping.
 */

import React, { useEffect, useRef, useState } from "react";
import { attachAutocomplete } from "../../utils/places";
import MDBox from "../MDBox";
import MDTypography from "../MDTypography";

// Inject the .pac-container z-index fix exactly once per page load.
let _pacStyleInjected = false;
function ensurePacStyle() {
  if (_pacStyleInjected) return;
  const s = document.createElement("style");
  s.innerHTML = ".pac-container { z-index: 99999 !important; }";
  document.head.appendChild(s);
  _pacStyleInjected = true;
}

const BASE_STYLE = {
  width: "100%",
  padding: "11px 12px",
  borderRadius: "8px",
  border: "1px solid #d2d6da",
  fontSize: "0.875rem",
  color: "#495057",
  background: "#fff",
  outline: "none",
  fontFamily: "inherit",
  boxSizing: "border-box",
  transition: "border-color 0.15s ease",
};

export default function PlacesAutocompleteInput({
  label,
  value,
  onChange,
  placeholder,
  required,
  style: extraStyle,
  recentSuggestions,
  ...rest
}) {
  const inputRef    = useRef(null);
  const containerRef = useRef(null);

  const [showRecent, setShowRecent]     = useState(false);
  const [filteredRecent, setFiltered]   = useState([]);
  const [highlightIdx, setHighlightIdx] = useState(-1);

  // ── Attach Google Places autocomplete ────────────────────────────────────
  useEffect(() => {
    ensurePacStyle();

    const el = inputRef.current;
    if (!el) return;

    let cancelled = false;

    attachAutocomplete(el, (place) => {
      if (cancelled) return;
      const addr = place?.formatted_address || el.value;
      if (onChange) onChange(addr);
      setShowRecent(false);
    });

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Filter recent suggestions as user types ───────────────────────────────
  useEffect(() => {
    const suggestions = Array.isArray(recentSuggestions) ? recentSuggestions : [];
    if (!value || value.trim().length < 2 || suggestions.length === 0) {
      setFiltered([]);
      setShowRecent(false);
      return;
    }
    const q = value.toLowerCase();
    const matches = suggestions.filter(
      (s) =>
        (s.address || "").toLowerCase().includes(q) ||
        (s.clientName || "").toLowerCase().includes(q) ||
        (s.facilityName || "").toLowerCase().includes(q)
    );
    setFiltered(matches);
    setHighlightIdx(-1);
    setShowRecent(matches.length > 0);
  }, [value, recentSuggestions]);

  // ── Close on outside click ────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowRecent(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const selectSuggestion = (addr) => {
    if (onChange) onChange(addr);
    setShowRecent(false);
    setHighlightIdx(-1);
    // Move focus back to input so the user can continue if needed
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (!showRecent || filteredRecent.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIdx((i) => Math.min(i + 1, filteredRecent.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === "Enter" && highlightIdx >= 0) {
      e.preventDefault();
      selectSuggestion(filteredRecent[highlightIdx].address);
    } else if (e.key === "Escape") {
      setShowRecent(false);
    }
  };

  return (
    <MDBox ref={containerRef} sx={{ position: "relative" }}>
      {label && (
        <MDTypography
          component="label"
          variant="button"
          fontWeight="regular"
          color="text"
          display="block"
          mb={0.5}
        >
          {label}
        </MDTypography>
      )}
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => {
          if (onChange) onChange(e.target.value);
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder || ""}
        required={required}
        onFocus={(e) => {
          e.target.style.borderColor = "#1A73E8";
          // Re-show suggestions if there are matches and field already has text
          if (filteredRecent.length > 0) setShowRecent(true);
        }}
        onBlur={(e) => {
          e.target.style.borderColor = "#d2d6da";
        }}
        style={{ ...BASE_STYLE, ...extraStyle }}
        {...rest}
      />

      {/* Recent address suggestions dropdown */}
      {showRecent && filteredRecent.length > 0 && (
        <ul
          style={{
            position:   "absolute",
            top:        "100%",
            left:       0,
            right:      0,
            zIndex:     99998,
            margin:     "2px 0 0",
            padding:    0,
            listStyle:  "none",
            background: "#fff",
            border:     "1px solid #d1d5db",
            borderRadius: 8,
            boxShadow:  "0 4px 16px rgba(0,0,0,0.10)",
            maxHeight:  220,
            overflowY:  "auto",
          }}
        >
          <li
            style={{
              padding:    "5px 12px",
              fontSize:   10,
              fontWeight: 700,
              letterSpacing: "0.05em",
              color:      "#9ca3af",
              background: "#f9fafb",
              borderBottom: "1px solid #f3f4f6",
              userSelect: "none",
            }}
          >
            RECENTLY USED
          </li>
          {filteredRecent.map((s, idx) => (
            <li
              key={idx}
              onMouseDown={(e) => {
                e.preventDefault();
                selectSuggestion(s.address);
              }}
              onMouseEnter={() => setHighlightIdx(idx)}
              style={{
                padding:    "8px 12px",
                cursor:     "pointer",
                background: highlightIdx === idx ? "#eff6ff" : "#fff",
                borderBottom: idx < filteredRecent.length - 1 ? "1px solid #f3f4f6" : "none",
              }}
            >
              <div
                style={{
                  fontSize:   13,
                  fontWeight: 500,
                  color:      "#1e293b",
                  whiteSpace: "nowrap",
                  overflow:   "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {s.address}
              </div>
              {(s.clientName || s.facilityName) && (
                <div
                  style={{
                    fontSize:   11,
                    color:      "#6b7280",
                    marginTop:  1,
                    whiteSpace: "nowrap",
                    overflow:   "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {[s.facilityName, s.clientName].filter(Boolean).join(" · ")}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </MDBox>
  );
}
